import { FC, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { QueryErrorResetBoundary, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { ErrorBoundary } from 'react-error-boundary'
import { Alert, Text, Stack, Button, Group, NativeSelect } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { useForm } from '@mantine/form'
import { ascend, descend, prop, sortWith } from 'ramda'
import { useInterval } from '@mantine/hooks'
import { add, isAfter } from 'date-fns'

import { Driver } from '@/api/navigationApi/types.gen'
import { CenteredLoader } from '@/components/CenteredLoader'

import { driverSignInMutation, getDriversOptions } from '@/api/navigationApi'
import { Client } from '@/api/main/client'
import AlertContainer from './AlertContainer'

const LAST_DRIVER_KEY = 'last-signed-in-driver'

const getLastDriverId = (): string | null => {
  try {
    return localStorage.getItem(LAST_DRIVER_KEY)
  } catch {
    return null
  }
}

const setLastDriverId = (id: string) => {
  try {
    localStorage.setItem(LAST_DRIVER_KEY, id)
  } catch {
    // localStorage unavailable; ignore
  }
}

const DriverSelectionInner: FC<{ client: Client | undefined }> = ({ client }) => {
  const { t } = useTranslation(['navigation', 'errors'])
  const [isDriverSelected, setDriverSelected] = useState(false)

  const {
    data: { drivers, shifts },
  } = useSuspenseQuery({
    ...getDriversOptions({ client }),
    refetchInterval: 200,
    staleTime: 200,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })

  const [currentTime, setCurrentTime] = useState(new Date())
  const updateCurrentTime = useInterval(() => setCurrentTime(new Date()), 15 * 1000)

  useEffect(() => {
    updateCurrentTime.start()
    return updateCurrentTime.stop
    // updateCurrentTime is a stable @mantine/hooks interval ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const currentShift = useMemo(
    () =>
      // Try latest shifts first to avoid matching a just-finished shift
      sortWith([descend(prop('starting_from'))], shifts).find(
        it =>
          isAfter(add(currentTime, { minutes: 45 }), it.starting_from) && // Shift starts in 45 minutes or less
          isAfter(add(it.starting_from, { minutes: 180 }), currentTime) && // Shift should have started at most 3 hours ago
          isAfter(it.ending_at, currentTime)
      ),
    [shifts, currentTime]
  )

  const nextUpcomingShift = useMemo(
    () =>
      sortWith([ascend(prop('starting_from'))], shifts).find(
        it => it.driver_id != null && isAfter(it.starting_from, currentTime)
      ),
    [shifts, currentTime]
  )

  const fallbackDriverId = useMemo(() => {
    if (nextUpcomingShift?.driver_id != null) {
      const id = nextUpcomingShift.driver_id.toString()
      if (drivers[id]) return id
    }
    const lastId = getLastDriverId()
    if (lastId && drivers[lastId]) return lastId
    return Object.values(drivers)[0]?.id.toString()
  }, [drivers, nextUpcomingShift])

  const currentShiftDriverRef = useRef(currentShift?.driver_id)
  useEffect(() => {
    if (currentShiftDriverRef.current !== currentShift?.driver_id) {
      form.setFieldValue('driverId', currentShift?.driver_id?.toString() ?? fallbackDriverId ?? '')
      currentShiftDriverRef.current = currentShift?.driver_id
    }
    // form is a stable Mantine form ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShift, fallbackDriverId])

  const { mutate: signIn, status } = useMutation({
    ...driverSignInMutation({ client }),
    onSuccess: (_data, variables) => {
      setLastDriverId(String(variables.body.driverId))
    },
  })

  const form = useForm({
    initialValues: {
      driverId: currentShift ? drivers[currentShift.driver_id ?? '']?.id.toString() : fallbackDriverId,
    },
    validate: {
      driverId: (it: string | undefined) =>
        it !== undefined ? null : t('noDriverSelected', { ns: 'errors', defaultValue: 'No driver selected' }),
    },
  })

  const selectedDriver: Driver | undefined = drivers[form.values.driverId]

  if (isDriverSelected && selectedDriver !== undefined) {
    return (
      <Stack gap="xl" p="xl">
        <Text size="xl">{selectedDriver.name}</Text>
        <Group>
          <Button color="red" onClick={() => setDriverSelected(false)} size="xl">
            {t('no')}
          </Button>
          <Button
            color="green"
            onClick={() =>
              signIn({
                body: { driverId: selectedDriver.id },
              })
            }
            size="xl"
            loading={status === 'pending'}
          >
            {t('yes')}
          </Button>
        </Group>
      </Stack>
    )
  }

  return (
    <form
      onSubmit={form.onSubmit(() => {
        setDriverSelected(true)
      })}
    >
      <Stack gap="xl" p="xl">
        <Text size="var(--spacing-lg)">{t('driverSelectionHeading')}</Text>
        <NativeSelect
          label={t('selectDriver')}
          labelProps={{ size: 'var(--spacing-l)', c: 'red' }}
          size="xl"
          data={sortWith([ascend(prop('name'))], Object.values(drivers)).map(it => ({
            value: it.id.toString(),
            label: it.name,
          }))}
          {...form.getInputProps('driverId')}
        />
        <Button type="submit" size="xl">
          {t('ok')}
        </Button>
      </Stack>
    </form>
  )
}

const DriverSelection: FC<{
  client: Client | undefined
}> = ({ client }) => {
  const { t } = useTranslation(['navigation', 'errors'])

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <AlertContainer type="message" noOverlay height="large">
          <ErrorBoundary
            onReset={reset}
            fallbackRender={({ resetErrorBoundary }) => (
              <Alert title={t('couldNotFetchDriverList', { ns: 'errors' })}>
                <Button onClick={resetErrorBoundary}>Try again</Button>
              </Alert>
            )}
          >
            <Suspense fallback={<CenteredLoader />}>
              <DriverSelectionInner client={client} />
            </Suspense>
          </ErrorBoundary>
        </AlertContainer>
      )}
    </QueryErrorResetBoundary>
  )
}

export default DriverSelection
